export type AuthMode = 'cookie' | 'bearer'
export type ClientPlatform = 'web' | 'ios' | 'android' | 'api'

function truthy(value: string | undefined): boolean {
  return value?.trim().toLowerCase() === 'true'
}

function isClientPlatform(value: string | undefined): value is ClientPlatform {
  return value === 'web' || value === 'ios' || value === 'android' || value === 'api'
}

function isTauri(): boolean {
  return (
    typeof window !== 'undefined' &&
    '__TAURI_INTERNALS__' in (window as unknown as Record<string, unknown>)
  )
}

function normalizeBaseUrl(value: string | undefined): string {
  const fallback = '/api/v1'
  const raw = value?.trim()
  if (!raw) return fallback
  return raw.endsWith('/') ? raw.slice(0, -1) : raw
}

/**
 * Public build-time configuration. Nothing in this module may contain a
 * secret: every VITE_* value is shipped to the browser. Provider flags are
 * false by default so missing credentials degrade gracefully (D-044).
 *
 * Native builds should set VITE_CLIENT_PLATFORM=ios|android until FE-TAURI
 * replaces the guess with the OS plugin. `api` still gets bearer tokens, but
 * the platform-specific store/push code needs the exact value.
 */
const tauri = isTauri()
const configuredPlatform = import.meta.env.VITE_CLIENT_PLATFORM
const clientPlatform: ClientPlatform = isClientPlatform(configuredPlatform)
  ? configuredPlatform
  : tauri
    ? 'api'
    : 'web'

export const env = {
  apiBaseUrl: normalizeBaseUrl(import.meta.env.VITE_API_BASE_URL),
  wsUrl: import.meta.env.VITE_WS_URL?.trim() ?? '',
  /**
   * Public app version compared with `/app-config.minimumAppVersion`. Native
   * release builds inject their real version; the package default keeps the
   * update gate meaningful in development.
   */
  appVersion: import.meta.env.VITE_APP_VERSION?.trim() || '1.0.0',
  authMode: (import.meta.env.VITE_AUTH_MODE ??
    (tauri ? 'bearer' : 'cookie')) as AuthMode,
  clientPlatform,
  isTauri,
  isDev: import.meta.env.DEV,
  /**
   * The bundled native shell serves the app from its own origin and has no
   * browser push subscription, so the PWA worker is web-only. Registering it
   * inside the WebView can pin a stale app shell across upgrades.
   */
  enableServiceWorker:
    !tauri && (!import.meta.env.DEV || truthy(import.meta.env.VITE_ENABLE_SW)),
  providers: {
    google: truthy(import.meta.env.VITE_GOOGLE_OAUTH_ENABLED),
    googleClientId: import.meta.env.VITE_GOOGLE_CLIENT_ID?.trim() ?? '',
    apple: truthy(import.meta.env.VITE_APPLE_OAUTH_ENABLED),
    nativePush: truthy(import.meta.env.VITE_NATIVE_PUSH_ENABLED),
  },
} as const
